<script setup>
import { ref, provide, inject, computed } from "vue";
import { FilterIconWithText } from "@/components/svg-icons";
import DrawerModal from "@/components/modals/DrawerModal";
import InferenceFilters from "./FilterDrawer/InferenceFilters.vue";
import Tag from "primevue/tag";
import { INFERENCE_FILTERS, INITIAL_INFERENCE_FILTERS } from "../constants";

const clearFilters = ref(false);
const saveFilters = ref(false);
const isOpen = ref(false);

provide("clearFilters", clearFilters);
provide("saveFilters", saveFilters);

const triggerHandleSeeResultsClick = () => {
  saveFilters.value = !saveFilters.value;
  toggleModal();
};

const triggerHandleClearFiltersClick = () => {
  clearFilters.value = !clearFilters.value;
  toggleModal();
};

const toggleModal = () => {
  isOpen.value = !isOpen.value;
};

const inferenceFilters = inject("inferenceFilters");

const clearAllFilters = () => {
  inferenceFilters.value = { ...INITIAL_INFERENCE_FILTERS };
};

const activeFilters = computed(() => {
  return INFERENCE_FILTERS.filter((filter) => {
    const filterValues = inferenceFilters.value[filter.filterPath];
    return filterValues && filterValues.length > 0;
  });
});

const shouldShowFilterPills = computed(() => {
  return activeFilters.value.length > 0;
});

const getFilterItemCount = (filterPath) => {
  return inferenceFilters.value[filterPath].length;
};
</script>

<template>
  <div class="flex flex-1 items-center gap-2">
    <button
      aria-controls="expand-filters"
      :aria-expanded="isOpen"
      data-testid="filter-icon"
      class="flex text-lg"
      @click="toggleModal"
    >
      <FilterIconWithText />
    </button>

    <div v-if="shouldShowFilterPills" class="flex items-center flex-wrap gap-2 ml-2">
      <Tag
        v-for="filter in activeFilters"
        :key="filter.filterPath"
        class="!bg-gray-275 !text-indigo-base px-3 py-1 !font-normal !rounded-full flex items-center"
      >
        {{ filter.title }}
        <span
          class="!bg-indigo-base !text-white !font-semibold rounded-full ml-1.5 px-1.5 py-0.5 text-xs inline-flex items-center justify-center min-w-5 h-5"
        >
          {{ getFilterItemCount(filter.filterPath) }}
        </span>
      </Tag>
      <button class="font-semibold text-university-secondary !text-sm" @click.prevent="clearAllFilters">
        CLEAR ALL
      </button>
    </div>
  </div>

  <DrawerModal :is-open="true" :show="isOpen" title="approve courses filter" drawer-width-class="md:w-[463px]" @close="toggleModal">
    <template #modal-content>
      <div class="flex flex-nowrap flex-col w-full px-[40px] pt-[24px] md:pb-[78px]">
        <InferenceFilters />
      </div>
    </template>
    <template #modal-footer>
      <div class="flex items-center justify-between px-12 w-full">
        <div class="flex">
          <button
            class="bg-white hover:bg-blue-350 px-10 py-2 gap-2 w-[125px] md:w-[180px] h-[40px] uppercase font-montserrat font-semibold text-xs md:text-sm leading-5 text-teal-900"
            data-testid="filter-modal-clear-button"
            @click.prevent="triggerHandleClearFiltersClick"
          >
            CLEAR
          </button>
        </div>
        <div class="flex">
          <button
            class="bg-teal-900 hover:bg-white px-10 py-2 gap-2 w-[125px] md:w-[180px] h-[40px] font-montserrat font-semibold uppercase text-xs md:text-sm leading-5 text-white hover:text-teal-900"
            data-testid="filter-modal-results-button"
            @click.prevent="triggerHandleSeeResultsClick"
          >
            SEE RESULTS
          </button>
        </div>
      </div>
    </template>
  </DrawerModal>
</template>
